import { Drawer, type DrawerProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useRef, useState, type ReactNode, type TouchEvent } from 'react';
import { Modal } from '../../atoms/Modal';
import type { CoreProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';
import { usePlatform } from '../../../hooks/usePlatform';

export interface BottomDrawerProps extends CoreProps {
  opened: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  size?: DrawerProps['size'];
}

export function BottomDrawer({
  opened,
  onClose,
  title,
  children,
  size = 'auto',
  testId,
  view = 'all',
}: BottomDrawerProps) {
  const shouldRender = useShouldRender(view);
  const platform = usePlatform();
  const isMobilePlatform = platform === 'mobile';
  const isTablet = useMediaQuery('(max-width: 1024px)');
  const isCompact = isMobilePlatform || Boolean(isTablet);

  const touchStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  function handleTouchStart(e: TouchEvent<HTMLDivElement>) {
    touchStartY.current = e.touches[0].clientY;
    setDragOffset(0);
  }

  function handleTouchMove(e: TouchEvent<HTMLDivElement>) {
    if (touchStartY.current === null) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0) setDragOffset(delta);
  }

  function handleTouchEnd() {
    if (dragOffset > 80) onClose();
    touchStartY.current = null;
    setDragOffset(0);
  }

  if (!shouldRender) return null;

  // Map drawer sizes to modal widths on desktop (xl wizard needs more room)
  const modalSize =
    size === 'xl' || size === '100%' || size === '90%'
      ? 'lg'
      : size === 'lg'
        ? 'lg'
        : size === 'sm' || size === 'xs'
          ? size
          : 'md';

  const sheetMaxHeight =
    size === 'xl' || size === '100%' || size === '90%' ? '92vh' : '80vh';

  // ── Desktop: standard centered modal ──────────────────────────────────────
  if (!isCompact) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={title}
        data-testid={testId}
        centered
        size={modalSize}
      >
        {children}
      </Modal>
    );
  }

  // ── Mobile / Tablet: bottom-sheet drawer with swipe-to-close ──────────────
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      size={size}
      radius="md"
      title={title}
      data-testid={testId}
      styles={{
        content: {
          maxHeight: sheetMaxHeight,
          height: size === 'xl' ? sheetMaxHeight : 'auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: dragOffset > 0 ? 'none' : 'transform 200ms ease',
          touchAction: 'none',
        },
        body: {
          overflowY: 'auto',
          flex: 1,
          paddingBottom: 24,
        },
        header: {
          paddingBottom: 4,
          flexShrink: 0,
        },
      }}
    >
      {/* Drag handle — swipe downward > 80px to close */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '4px 0 12px',
          cursor: 'grab',
          flexShrink: 0,
          userSelect: 'none',
        }}
      >
        <div
          style={{
            width: 40,
            height: 4,
            borderRadius: 2,
            background: 'var(--mantine-color-gray-4)',
          }}
        />
      </div>
      {children}
    </Drawer>
  );
}
